import { px } from '@adsk/alloy-react-helpers';

import {
  ActivityLogDesignTokens,
  AlloyLegacyThemeDesignTokens,
} from '../types';
import type { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getActivityLogTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ActivityLogDesignTokens => {
  const { typography, spacing, borderRadius } = theme;
  const { colors } = theme.tokens;

  return {
    'background-color': colors.background.primary.value,
    timeline: {
      'dot-border-color': colors.border.value,
      'dot-border-width': '2px',
      'dot-border-radius': borderRadius.circle,
      'line-color': colors.border.subtle.value,
      'padding-left': px(spacing.S),
    },
    content: {
      'padding-top': px(spacing.M),
      'padding-right': px(spacing.S),
      'padding-bottom': px(spacing.M),
      'padding-left': px(spacing.S),
      'footer-margin-top': px(spacing.S),
    },
    loader: {
      /**
       * Off-scale by design: carried over verbatim from the pre-migration
       * `paddingLeft: 25` so Alloy stays pixel-identical to `master`. Weave
       * snaps to the nearest scale step (24px) instead of inheriting this.
       */
      'timeline-padding-left': '25px',
      'content-padding-top': px(spacing.M),
      'content-padding-right': px(spacing.M),
      'content-padding-bottom': px(spacing.M),
      'content-padding-left': px(spacing.M),
    },
    header: {
      'font-family': typography.heading3.fontFamily,
      'font-size': typography.heading3.fontSize,
      'font-weight': typography.heading3.fontWeight,
      'line-height': typography.heading3.lineHeight,
      color: colors.text.secondary.value,
      'margin-bottom': px(spacing.XS),
    },
    footer: {
      'font-family': typography.caption.fontFamily,
      'font-size': typography.caption.fontSize,
      'font-weight': typography.caption.fontWeight,
      'line-height': typography.caption.lineHeight,
      color: colors.text.tertiary.value,
    },
    listRow: {
      'margin-bottom': px(spacing.XS),
      label: {
        'font-family': typography.bodyMedium.fontFamily,
        'font-size': typography.bodyMedium.fontSize,
        'font-weight': typography.bodyMedium.fontWeight,
        'line-height': typography.bodyMedium.lineHeight,
        color: colors.text.secondary.value,
        width: '116px',
        'margin-right': px(spacing.XS),
      },
      value: {
        'font-family': typography.bodyMedium.fontFamily,
        'font-size': typography.bodyMedium.fontSize,
        'font-weight': typography.bodyMedium.fontWeight,
        'line-height': typography.bodyMedium.lineHeight,
        color: colors.text.primary.value,
      },
    },
    status: {
      'dot-border-radius': px(borderRadius.medium),
      'margin-left': px(spacing.XS),
      label: {
        'font-family': typography.labelMedium.fontFamily,
        'font-size': typography.labelMedium.fontSize,
        'font-weight': typography.labelMedium.fontWeight,
        'line-height': typography.labelMedium.lineHeight,
        color: colors.text.primary.value,
      },
      subtext: {
        'font-family': typography.bodySmall.fontFamily,
        'font-size': typography.bodySmall.fontSize,
        'font-weight': typography.bodySmall.fontWeight,
        'line-height': typography.bodySmall.lineHeight,
        color: colors.text.secondary.value,
      },
    },
    user: {
      'font-family': typography.labelMedium.fontFamily,
      'font-size': typography.labelMedium.fontSize,
      'font-weight': typography.labelMedium.fontWeight,
      'line-height': typography.labelMedium.lineHeight,
      color: colors.text.primary.value,
      'removed-color': colors.text.inactive.value,
      'avatar-margin-top': px(spacing.XXXS),
      'margin-left': px(spacing.XS),
    },
    error: {
      'font-family': typography.bodySmall.fontFamily,
      'font-size': typography.bodySmall.fontSize,
      'font-weight': typography.bodySmall.fontWeight,
      'line-height': typography.bodySmall.lineHeight,
      color: colors.text.secondary.value,
      'padding-left': px(spacing.XS),
      'padding-right': px(spacing.XS),
    },
    photoLabel: {
      'font-family': typography.caption.fontFamily,
      'font-size': typography.caption.fontSize,
      'font-weight': typography.caption.fontWeight,
      'line-height': typography.caption.lineHeight,
      color: colors.text.secondary.value,
      /**
       * Off-scale by design: carried over verbatim from the pre-migration
       * `marginTop: 6` so Alloy stays pixel-identical to `master`. Weave snaps
       * to the nearest scale step (4px) instead of inheriting this.
       */
      'margin-top': '6px',
    },
    seeMore: {
      'font-family': typography.bodyMedium.fontFamily,
      'font-size': typography.bodyMedium.fontSize,
      'font-weight': typography.bodyMedium.fontWeight,
      'line-height': typography.bodyMedium.lineHeight,
      color: colors.text.primary.value,
      'margin-top': px(spacing.XXS),
    },
    multiUserChange: {
      'description-margin-bottom-truncated': px(spacing.XXS),
      'description-margin-bottom-expanded': px(spacing.S),
      'toggle-margin-top-expanded': px(spacing.XXS),
    },
  };
};

export const getActivityLogTokensWeave = (
  tokens: WeaveTokens | undefined,
  fontFamily: string,
): ActivityLogDesignTokens => {
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;
  const typography = tokens?.component?.typography;

  return {
    'background-color': semantic?.['background-color']?.surface?.[100],
    timeline: {
      'dot-border-color': semantic?.['border-color']?.medium?.default,
      'dot-border-width': generic?.['border-width']?.[2],
      'dot-border-radius': generic?.['border-radius']?.ellipse,
      'line-color': semantic?.['border-color']?.divider?.light,
      'padding-left': generic?.spacing?.[8],
    },
    content: {
      'padding-top': generic?.spacing?.[16],
      'padding-right': generic?.spacing?.[8],
      'padding-bottom': generic?.spacing?.[16],
      'padding-left': generic?.spacing?.[8],
      'footer-margin-top': generic?.spacing?.[8],
    },
    loader: {
      'timeline-padding-left': generic?.spacing?.[24],
      'content-padding-top': generic?.spacing?.[16],
      'content-padding-right': generic?.spacing?.[16],
      'content-padding-bottom': generic?.spacing?.[16],
      'content-padding-left': generic?.spacing?.[16],
    },
    header: {
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[16],
      'font-weight': typography?.medium?.['font-weight'],
      'line-height': generic?.['line-height']?.[22],
      color: semantic?.['text-color']?.medium?.default,
      'margin-bottom': generic?.spacing?.[8],
    },
    footer: {
      'font-family': fontFamily,
      'font-size': typography?.caption?.['font-size'],
      'font-weight': typography?.regular?.['font-weight'],
      'line-height': typography?.caption?.['line-height'],
      color: semantic?.['text-color']?.light,
    },
    listRow: {
      'margin-bottom': generic?.spacing?.[8],
      label: {
        'font-family': fontFamily,
        'font-size': typography?.body?.['font-size'],
        'font-weight': typography?.regular?.['font-weight'],
        'line-height': typography?.body?.['line-height'],
        color: semantic?.['text-color']?.light,
        width: generic?.spacing?.[128],
        'margin-right': generic?.spacing?.[8],
      },
      value: {
        'font-family': fontFamily,
        'font-size': typography?.body?.['font-size'],
        'font-weight': typography?.regular?.['font-weight'],
        'line-height': typography?.body?.['line-height'],
        color: semantic?.['text-color']?.medium?.default,
      },
    },
    status: {
      'dot-border-radius': generic?.['border-radius']?.[2],
      'margin-left': generic?.spacing?.[8],
      label: {
        'font-family': fontFamily,
        'font-size': typography?.body?.['font-size'],
        'font-weight': typography?.medium?.['font-weight'],
        'line-height': typography?.body?.['line-height'],
        color: semantic?.['text-color']?.medium?.default,
      },
      subtext: {
        'font-family': fontFamily,
        'font-size': typography?.caption?.['font-size'],
        'font-weight': typography?.regular?.['font-weight'],
        'line-height': typography?.caption?.['line-height'],
        color: semantic?.['text-color']?.light,
      },
    },
    user: {
      'font-family': fontFamily,
      'font-size': typography?.body?.['font-size'],
      'font-weight': typography?.medium?.['font-weight'],
      'line-height': typography?.body?.['line-height'],
      color: semantic?.['text-color']?.medium?.default,
      'removed-color': semantic?.['text-color']?.placeholder,
      'avatar-margin-top': generic?.spacing?.[2],
      'margin-left': generic?.spacing?.[8],
    },
    error: {
      'font-family': fontFamily,
      'font-size': typography?.caption?.['font-size'],
      'font-weight': typography?.regular?.['font-weight'],
      'line-height': typography?.caption?.['line-height'],
      color: semantic?.['text-color']?.light,
      'padding-left': generic?.spacing?.[8],
      'padding-right': generic?.spacing?.[8],
    },
    photoLabel: {
      'font-family': fontFamily,
      'font-size': typography?.caption?.['font-size'],
      'font-weight': typography?.regular?.['font-weight'],
      'line-height': typography?.caption?.['line-height'],
      color: semantic?.['text-color']?.light,
      'margin-top': generic?.spacing?.[4],
    },
    seeMore: {
      'font-family': fontFamily,
      'font-size': typography?.body?.['font-size'],
      'font-weight': typography?.regular?.['font-weight'],
      'line-height': typography?.body?.['line-height'],
      color: semantic?.['text-color']?.medium?.default,
      'margin-top': generic?.spacing?.[4],
    },
    multiUserChange: {
      'description-margin-bottom-truncated': generic?.spacing?.[4],
      'description-margin-bottom-expanded': generic?.spacing?.[12],
      'toggle-margin-top-expanded': generic?.spacing?.[4],
    },
  };
};
